import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import EmailIcon from "@material-ui/icons/Email";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useFormik } from "formik";
import { getErrorMessage, isError } from "libs/utils/form";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { useHistory } from "react-router-dom";
import { clearLoginAsyncState, loginOffline } from "redux/modules/authSlice";
import * as yup from "yup";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flex: 1,
    height: "100vh",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `url(./assets/images/bg-login.png), linear-gradient(to bottom left, ${theme.palette.primary.main}, #00c2cb 45%, ${theme.palette.secondary.main})`,
    objectFit: "cover",
    backgroundSize: "cover",
    backgroundPosition: "50% 50%",
    backgroundRepeat: "no-repeat",
    backgroundBlendMode: "screen",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 3px 17px 0 rgba(44, 40, 40, 0.11)",

    paddingLeft: 25,
    paddingRight: 25,
    paddingTop: 25,
    paddingButton: 45,
    flex: 1,
    borderRadius: 10,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    alignItems: "flex-start",
    width: 470,
  },
  logo: {
    display: "flex",
    alignSelf: "center",
    width: "300px !important",
    margin: theme.spacing(4),
  },
  textField: {
    width: 460,
    marginTop: theme.spacing(5),
  },
  title: {
    fontSize: 55,
    color: "#003b5c",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    height: 50,
    width: 460,
    backgroundColor: "#11cdef",
    alignSelf: "center",
  },
  offlineLogin: {
    alignSelf: "center",
  },
  loading: {
    marginRight: theme.spacing(1),
  },
  line: {
    marginTop: 8,
    height: 3,
    width: 50,
    backgroundColor: "#11cdef",
  },
  emailIcon: {
    width: 80,
    height: 80,
    alignSelf: "center",
    color: theme.palette.warning.main,
  },
}));

export default function Login() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedItem, setSelectedItem] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const { isLoading, isSuccess, isFailure } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    dispatch(clearLoginAsyncState());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && isSuccess) {
      dispatch(clearLoginAsyncState());
      history.replace("/");
    }
  }, [isSuccess, isLoading, history, dispatch, isFailure]);

  const validationSchema = yup.object({
    username: yup
      .string()
      .required("Email is required")
      .email("Email is invalid format"),
    password: yup.string().required("Password is required"),
  });
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      username: "",
      password: "",
    },
    isInitialValid: false,
    onSubmit: async (values) => {
      dispatch(loginOffline(values.username, values.password));
    },
  });

  const handleOnEnterEmail = () => {
    setSelectedItem(1);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className={classes.root}>
        <Container maxWidth="sm">
          <Card className={classes.paper}>
            <CardContent>
              <Carousel
                showArrows={false}
                autoFocus={false}
                autoPlay={false}
                centerMode={false}
                infiniteLoop={false}
                emulateTouch={false}
                showStatus={false}
                showIndicators={false}
                showThumbs={false}
                swipeable={false}
                selectedItem={selectedItem}
                renderArrowPrev={() => null}
                renderArrowNext={() => null}
                renderThumbs={() => null}
                renderIndicator={() => null}
                width={470}
              >
                <div className={classes.content}>
                  <Typography variant="h4" className={classes.title}>
                    Login
                  </Typography>
                  <Typography variant="h6">
                    Smart VISA Service (for offline user)
                  </Typography>
                  <div className={classes.line} />
                  <img
                    alt="smart-visa"
                    src="/assets/images/logo.png"
                    className={classes.logo}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    name="username"
                    placeholder="Email address"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={isError("username", formik)}
                    helperText={getErrorMessage("username", formik)}
                    onKeyDown={(e) => {
                      if (!formik.errors.username && e.key === "Enter") {
                        handleOnEnterEmail();
                      } else if (e.keyCode === 9) {
                        e.preventDefault();
                      }
                    }}
                  />

                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    type="button"
                    fullWidth
                    className={classes.submit}
                    onClick={() => handleOnEnterEmail()}
                    disabled={
                      isLoading || !formik.dirty || formik.errors.username
                    }
                  >
                    {isLoading && (
                      <CircularProgress
                        color="secondary"
                        className={classes.loading}
                        size={18}
                      />
                    )}
                    Continue
                  </Button>
                </div>
                <div className={classes.content}>
                  <EmailIcon className={classes.emailIcon} />
                  <div
                    style={{
                      display: "flex",
                      height: 150,
                      width: "100%",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: 18,
                    }}
                  >
                    <Typography variant="h6">
                      Password is already send to your email (
                      {formik.values.username})
                    </Typography>
                    <Typography
                      variant="body"
                      style={{ marginTop: 18 }}
                      color="textSecondary"
                    >
                      Please checking you inbox
                    </Typography>
                    <Typography variant="body" color="textSecondary">
                      or
                    </Typography>
                    <Typography variant="body" color="textSecondary">
                      back to enter email again
                    </Typography>
                  </div>
                  <TextField
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    name="password"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={isError("password", formik)}
                    helperText={getErrorMessage("password", formik)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    type="submit"
                    fullWidth
                    className={classes.submit}
                    onClick={() => setSelectedItem(1)}
                    disabled={isLoading || !formik.isValid}
                  >
                    {isLoading && (
                      <CircularProgress
                        color="secondary"
                        className={classes.loading}
                        size={18}
                      />
                    )}
                    Submit
                  </Button>
                  <Button
                    color="secondary"
                    size="large"
                    className={classes.offlineLogin}
                    fullWidth
                    disabled={isLoading}
                    onClick={() => setSelectedItem(0)}
                  >
                    Back
                  </Button>
                </div>
              </Carousel>
            </CardContent>
          </Card>
        </Container>
      </div>
    </form>
  );
}
